import { singleton, inject } from "tsyringe";

// shared
import type { HttpAdapter } from "@/shared/libs/http/interface";

import type {
    AiTutorSessionRepository,
    GetAiTutorSessionMessagesRequestDto,
    GetAiTutorSessionsRequestDto,
} from "@/entities/ai-tutor-session/domain/repository";
import type {
    GetAiTutorSessionMessagesResponseDto,
    GetAiTutorSessionsResponseDto,
} from "@/entities/ai-tutor-session/domain/dtos";
import {
    GetAiTutorSessionMessagesRequestDtoSchema,
    GetAiTutorSessionsRequestDtoSchema,
} from "@/entities/ai-tutor-session/infrastructure/schema";

@singleton()
export default class AiTutorSessionRepositoryImpl implements AiTutorSessionRepository {
    constructor(@inject("HttpAdapter") private readonly httpAdaptor: HttpAdapter) {}

    public getAiTutorSessionMessages = async (
        payload: GetAiTutorSessionMessagesRequestDto,
    ): Promise<GetAiTutorSessionMessagesResponseDto> => {
        /** schema 검증을 진행합니다. */
        const validatedPayload = GetAiTutorSessionMessagesRequestDtoSchema.safeParse(payload);

        if (validatedPayload.error) {
            throw new Error(`schema validation error: ${validatedPayload.error.message}`);
        }

        let url = `/v1/ai-tutor-sessions/${validatedPayload.data.aiTutorSessionId}/ai-tutor-session-messages?size=${validatedPayload.data.size}`;

        url += `&orders=${encodeURIComponent(JSON.stringify([{ createdAt: validatedPayload.data.orders }]))}`;

        if (validatedPayload.data.nextToken) {
            url += `&nextToken=${validatedPayload.data.nextToken}`;
        }

        const response = await this.httpAdaptor.getInBff<GetAiTutorSessionMessagesResponseDto>(url);

        return response;
    };

    public getAiTutorSessions = async (payload: GetAiTutorSessionsRequestDto) => {
        const validatedPayload = GetAiTutorSessionsRequestDtoSchema.safeParse(payload);

        if (validatedPayload.error) {
            throw new Error(`schema validation error: ${validatedPayload.error.message}`);
        }

        let url = `/v1/ai-tutor-sessions?userId=${validatedPayload.data.userId}&size=${validatedPayload.data.size}`;

        url += `&orders=${encodeURIComponent(JSON.stringify([{ [validatedPayload.data.orders.type]: validatedPayload.data.orders.order }]))}`;

        if (validatedPayload.data.startDate) {
            url += `&from=${validatedPayload.data.startDate}`;
        }
        if (validatedPayload.data.endDate) {
            url += `&to=${validatedPayload.data.endDate}`;
        }
        if (validatedPayload.data.bigUnitIds && validatedPayload.data.bigUnitIds.length > 0) {
            for (const bigUnitId of validatedPayload.data.bigUnitIds) {
                url += `&bigUnitId=${bigUnitId}`;
            }
        }

        if (validatedPayload.data.nextToken) {
            url += `&nextToken=${validatedPayload.data.nextToken}`;
        }

        const response = await this.httpAdaptor.getInBff<GetAiTutorSessionsResponseDto>(url);

        return response;
    };
}
